import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'reactstrap';
import { sendPaymentMethodChangeLink } from '../../../shared/actions/transactionAction';
import { ROUTES } from '../../../shared/constants/routes';

import './change-payout-method-modal.css';

const DEFAULT_ERROR_MESSAGE = 'Failed to send confirmation email. Please try again.';
const SUCCESS_MESSAGE =
  'Confirmation email sent. Check your inbox and click the link to confirm before updating your payout method.';

const ChangePayoutMethodModal = ({ isOpen, toggle, userEmail }) => {
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: string }

  useEffect(() => {
    if (!isOpen) return;
    setIsSending(false);
    setStatus(null);
  }, [isOpen]);

  const safeToggle = () => {
    if (isSending) return;
    if (typeof toggle === 'function') toggle();
  };

  const handleSendConfirmation = async () => {
    setIsSending(true);
    setStatus(null);

    try {
      const result = await sendPaymentMethodChangeLink();

      if (!result?.success) {
        throw new Error(result?.message || result?.error || DEFAULT_ERROR_MESSAGE);
      }

      setStatus({
        type: 'success',
        message: SUCCESS_MESSAGE,
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error?.message || DEFAULT_ERROR_MESSAGE,
      });
    } finally {
      setIsSending(false);
    }
  };

  const trimmedEmail = (userEmail || '').trim();
  const emailDisplay = trimmedEmail || 'Not available';
  const sent = status?.type === 'success';

  return (
    <Modal
      isOpen={isOpen}
      centered
      className="cm_g_modal_wrapper change-payout-method-modal"
      backdrop="static"
      keyboard={!isSending}
    >
      <div className="cm_g_modal_header">
        <h3>Change Payout Method</h3>
        <button
          type="button"
          className="cm_g_modal_close"
          onClick={safeToggle}
          disabled={isSending}
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      <div className="cm_g_modal_body">
        <p className="text-secondary mb-3">
          To change your payout method, we’ll send a confirmation link to your registered email.
          The change will only be applied after you verify it.
        </p>

        <div className="bb_card mb-3">
          <p className="mb-0 text-secondary">
            <b className="text-white">Email:</b> {emailDisplay}
          </p>
        </div>
        <p className="text-secondary text_14 mb-3">
          Your registered email cannot be changed after signup. If you need assistance, please{' '}
          <Link to={ROUTES.CONTACT_US} className="text-secondary">
            contact support
          </Link>
          .
        </p>

        {status?.message && (
          <div
            className={`cm_status_banner mb-3 ${status.type}`}
            role="status"
            aria-live="polite"
          >
            {status.message}
          </div>
        )}

        <div className="change-payout-method-actions d-flex gap-2 flex-column flex-sm-row">
          {!sent && (
            <button
              className="btn btn_cancel change-payout-method-cancel"
              type="button"
              onClick={safeToggle}
              disabled={isSending}
              aria-label="Cancel"
            >
              Cancel
            </button>
          )}

          {!sent && (
            <button
              className="btn btn_primary change-payout-method-primary"
              type="button"
              onClick={handleSendConfirmation}
              disabled={isSending}
              aria-label="Send confirmation email"
            >
              {isSending ? 'Sending…' : 'Send confirmation email'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ChangePayoutMethodModal;

